// App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useDispatch } from "react-redux";
import Home from "./pages/Home";
import SignIn from "./pages/SignIn";
import Best from "./pages/Best";
import Search from "./pages/Search";
import ProductView from "./pages/ProductView";
import Layout from "./components/Layout";
import SignUp from "./pages/SignUp";
import Checkout from "./pages/Checkout";
import Cart from "./pages/Cart";
import New from "./pages/New";
import ContactUs from "./pages/ContactUs";
import Deals from "./pages/Deals";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailsPage from "./pages/OrderDetails";
import { rehydrateAuth } from "./actions/authActions";
import { fetchSite } from "./actions/siteActions";
import { ImageProvider } from "./contexts/ImageContext";
import Category from "./pages/Category";
import TermsAndPoliciesPage from "./pages/Terms";
import MyAccount from "./pages/MyAccount";
import AccountInformation from "./pages/AccountInfo";
import SiteHead from "./components/SiteHead";
import ScrollToTop from "./components/ScrollToTop";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordFrom from "./components/ResetPasswordForm";
import WhatsappIcon from "./components/WhatsappIcon";
import Brands from "./pages/Brands";
import AllProducts from "./pages/AllProducts";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import Faqs from "./pages/Faqs";
import TrackOrder from "./pages/TrackOrder";
import PaymentCallback from "./pages/PaymentCallback";
import ReferralDashboard from "./pages/ReferralDashboard";
import CreateReferral from "./pages/CreateReferral";
import Routines from "./pages/Routines";
import RoutineDetail from "./pages/RoutineDetail";

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(rehydrateAuth());
    dispatch(fetchSite());
  }, [dispatch]);

  // Capture referral code from URL and store in localStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const referralCode = searchParams.get("rf");

    if (referralCode && referralCode.trim()) {
      // Clean and store/update referral code in localStorage with idp_prefix
      // This will update if there's a new referral code in the URL
      const cleanReferralCode = referralCode.trim();
      localStorage.setItem("idp_referralCode", cleanReferralCode);

      // Remove the rf parameter from URL after a short delay to avoid visible flash
      // Using requestAnimationFrame ensures it happens after the initial render
      requestAnimationFrame(() => {
        setTimeout(() => {
          const currentParams = new URLSearchParams(window.location.search);
          if (currentParams.get("rf")) {
            currentParams.delete("rf");
            const newUrl =
              window.location.pathname +
              (currentParams.toString() ? `?${currentParams.toString()}` : "");
            window.history.replaceState({}, "", newUrl);
          }
        }, 50);
      });
    }
    // If no referral code in URL, keep existing one in localStorage (don't clear it)
    // The referral code will persist across page loads until a new one is provided or order is placed
  }, []);

  return (
    <HelmetProvider>
      <ImageProvider>
        <Router>
          <SiteHead />
          <ScrollToTop />
          <WhatsappIcon />
          <Routes>
            <Route
              path="/"
              element={
                <Layout>
                  <Home />
                </Layout>
              }
            />
            <Route
              path="/category/:categoryId"
              element={
                <Layout>
                  <Category />
                </Layout>
              }
            />
            <Route
              path="/signin"
              element={
                <Layout>
                  <SignIn />
                </Layout>
              }
            />
            <Route
              path="/myaccount"
              element={
                <Layout>
                  <MyAccount />
                </Layout>
              }
            />
            <Route
              path="/signup"
              element={
                <Layout>
                  <SignUp />
                </Layout>
              }
            />
            <Route
              path="/best"
              element={
                <Layout>
                  <Best />
                </Layout>
              }
            />
            <Route
              path="/new"
              element={
                <Layout>
                  <New />
                </Layout>
              }
            />
            <Route
              path="/deals"
              element={
                <Layout>
                  <Deals />
                </Layout>
              }
            />
            <Route
              path="/search"
              element={
                <Layout>
                  <Search />
                </Layout>
              }
            />
            {/* Route accepts both slug (preferred) and product_id (backward compatible) */}
            <Route
              path="/product/:slug"
              element={
                <Layout>
                  <ProductView />
                </Layout>
              }
            />
            <Route
              path="/checkout"
              element={
                <Layout>
                  <Checkout />
                </Layout>
              }
            />
            <Route
              path="/accountinfo"
              element={
                <Layout>
                  <AccountInformation />
                </Layout>
              }
            />
            <Route
              path="/cart"
              element={
                <Layout>
                  <Cart />
                </Layout>
              }
            />
            <Route
              path="/contactus"
              element={
                <Layout>
                  <ContactUs />
                </Layout>
              }
            />
            <Route
              path="/orders"
              element={
                <Layout>
                  <OrdersPage />
                </Layout>
              }
            />
            <Route
              path="/terms"
              element={
                <Layout>
                  <TermsAndPoliciesPage />
                </Layout>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <Layout>
                  <OrderDetailsPage />
                </Layout>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <Layout>
                  <ForgotPassword />
                </Layout>
              }
            />
            <Route
              path="/reset-password"
              element={
                <Layout>
                  <ResetPasswordFrom />
                </Layout>
              }
            />
            <Route
              path="/brands"
              element={
                <Layout>
                  <Brands />
                </Layout>
              }
            />
            <Route
              path="/all-products"
              element={
                <Layout>
                  <AllProducts />
                </Layout>
              }
            />
            <Route
              path="/privacy"
              element={
                <Layout>
                  <Privacy />
                </Layout>
              }
            />
            <Route
              path="/legal"
              element={
                <Layout>
                  <Legal />
                </Layout>
              }
            />
            <Route
              path="/faqs"
              element={
                <Layout>
                  <Faqs />
                </Layout>
              }
            />
            <Route
              path="/track-order"
              element={
                <Layout>
                  <TrackOrder />
                </Layout>
              }
            />
            <Route
              path="/payment-callback"
              element={
                <Layout>
                  <PaymentCallback />
                </Layout>
              }
            />
            <Route
              path="/referral"
              element={
                <Layout>
                  <ReferralDashboard />
                </Layout>
              }
            />
            <Route
              path="/create-referral"
              element={
                <Layout>
                  <CreateReferral />
                </Layout>
              }
            />
            <Route
              path="/routines"
              element={
                <Layout>
                  <Routines />
                </Layout>
              }
            />
            <Route
              path="/routines/:routineId"
              element={
                <Layout>
                  <RoutineDetail />
                </Layout>
              }
            />
          </Routes>
        </Router>
      </ImageProvider>
    </HelmetProvider>
  );
};

export default App;
